import { t } from "@lingui/macro";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PublishIcon from "@mui/icons-material/Publish";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack,
  Step,
  Stepper,
  StepLabel,
  Chip,
  Grid,
} from "@mui/material";
import { AlertColor } from "@mui/material/Alert";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alerts from "@/client/components/alerts";
import { useUser } from "@/client/services/user";
import { color } from "framer-motion";

const CreateEditSelfAssessment = () => {
  const { id, step: stepParam } = useParams<{ id: string; step: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const studentId = user?.id;
  const [currentStep, setCurrentStep] = useState(0);
  const [assessmentId, setAssessmentId] = useState<string | null>(id || null);
  const [assessment, setAssessment] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    time: 10,
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const isEditMode = !!id;
  const steps = [t`Assessment Details`, t`Add Questions`, t`Publish`];

  useEffect(() => {
    if (stepParam) {
      setCurrentStep(parseInt(stepParam) || 0);
    }
    if (id) {
      fetchAssessment();
    }
  }, [id, stepParam]);

  const fetchAssessment = async () => {
    try {
      console.log("USER:", user);
      const res = await axios.get(
        `/api/integration/external-service/self-assessment/${id}`,
        {
          params: { studentId },
        }
      );
      const assessmentData = res.data.result;
      setAssessment(assessmentData);
      setAssessmentId(assessmentData._id);
      setFormData({
        name: assessmentData.name || "",
        description: assessmentData.description || "",
        category: assessmentData.category || "",
        time: assessmentData.time || 10,
      });
    } catch (error) {
      console.error("Error fetching assessment:", error);
      setAlert({
        open: true,
        message: t`Failed to fetch assessment details`,
        severity: "error",
      });
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateAssessment = async () => {
    if (!formData.name.trim()) {
      setAlert({
        open: true,
        message: t`Assessment name is required`,
        severity: "warning",
      });
      return;
    }
    if (!formData.category) {
      setAlert({
        open: true,
        message: t`Assessment category is required`,
        severity: "warning",
      });
      return;
    }
    if (!formData.time) {
      setAlert({
        open: true,
        message: t`Time limit is required`,
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        time: Number(formData.time),
        studentId,
      };

      if (isEditMode && id) {
        // Update existing
        await axios.patch(
          `/api/integration/external-service/self-assessment/${id}`,
          payload,
          {
            params: { studentId },
          }
        );
        setAlert({
          open: true,
          message: t`Assessment updated successfully`,
          severity: "success",
        });
        // Move to step 1 (Add Questions)
        setTimeout(() => {
          navigate(`/self-assessments/create/${id}/1`);
          setCurrentStep(1);
        }, 500);
        return;
      } else {
        // Create new
        const res = await axios.post(
          `/api/integration/external-service/self-assessment`,
          payload
        );
        const newId = res.data.result._id;
        setAssessmentId(newId);
        setAlert({
          open: true,
          message: t`Assessment created successfully`,
          severity: "success",
        });
        // Move to step 1 (Add Questions)
        setTimeout(() => {
          navigate(`/self-assessments/create/${newId}/1`);
          setCurrentStep(1);
        }, 500);
        return;
      }
    } catch (error: any) {
      console.error("Error saving assessment:", error);
      setAlert({
        open: true,
        message:
          error.response?.data?.error || t`Failed to save assessment`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    if (currentStep === 0) {
      handleCreateAssessment();
    } else if (currentStep === 1) {
      // Move to step 2 (Publish)
      navigate(`/self-assessments/create/${assessmentId}/2`);
      setCurrentStep(2);
    }
  };

  const handleSaveAsDraft = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        time: Number(formData.time),
        studentId,
      };
      await axios.patch(
        `/api/integration/external-service/self-assessment/${assessmentId}`,
        payload,
        {
          params: { studentId },
        }
      );
      setAlert({
        open: true,
        message: t`Assessment saved as draft`,
        severity: "success",
      });
      setTimeout(() => {
        navigate("/self-assessments");
      }, 1500);
    } catch (error: any) {
      console.error("Error saving assessment:", error);
      setAlert({
        open: true,
        message:
          error.response?.data?.error || t`Failed to save assessment`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        time: Number(formData.time),
        studentId,
        status: "published",
      };
      await axios.patch(
        `/api/integration/external-service/self-assessment/${assessmentId}`,
        payload,
        {
          params: { studentId },
        }
      );
      setAlert({
        open: true,
        message: t`Assessment published successfully`,
        severity: "success",
      });
      setTimeout(() => {
        navigate("/self-assessments");
      }, 1500);
    } catch (error: any) {
      console.error("Error publishing assessment:", error);
      setAlert({
        open: true,
        message:
          error.response?.data?.error || t`Failed to publish assessment`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      navigate(`/self-assessments/create/${assessmentId}/${prevStep}`);
      setCurrentStep(prevStep);
    } else {
      navigate("/self-assessments");
    }
  };

  const handleCloseAlert = () => {
    setAlert({
      open: false,
      message: "",
      severity: "info",
    });
  };

  return (
    <Box>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/self-assessments")}
            sx={{ textTransform: "none" }}
          >
            {t`Back to Self-Assessments`}
          </Button>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Card>
          <CardContent sx={{ p: 4 }}>
            {/* Step 0: Assessment Details */}
            {currentStep === 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                  {isEditMode
                    ? t`Edit Assessment Details`
                    : t`Create Assessment`}
                </Typography>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t`Assessment Name`}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t`e.g., JavaScript Fundamentals Practice`}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 48,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ccc !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#999 !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#555 !important",
                        },
                      "& .MuiOutlinedInput-root.Mui-focused": {
                        boxShadow: "none !important",
                        outline: "none !important",
                      },
                      "& input:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                      },
                    }}
                  />
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 48,
                      },
                    }}
                    required
                  >
                    <InputLabel>{t`Assessment Category`}</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      label={t`Assessment Category`}
                      required
                    >
                      <MenuItem value="practice">{t`Practice Test`}</MenuItem>
                      <MenuItem value="final">{t`Final Test`}</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    size="small"
                    label={t`Time Limit (minutes)`}
                    name="time"
                    type="number"
                    value={formData.time}
                    onChange={handleChange}
                    inputProps={{ min: 1 }}
                    required
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 45,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ccc !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#999 !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#555 !important",
                        },
                      "& .MuiOutlinedInput-root.Mui-focused": {
                        boxShadow: "none !important",
                        outline: "none !important",
                      },
                      "& input:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={t`Description`}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder={t`Brief description of what this assessment covers`}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#999",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#555",
                        },
                      },
                      "& .MuiInputBase-inputMultiline:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                        border: "none !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused textarea": {
                        outline: "none !important",
                      },
                    }}
                  />
                </Stack>
              </>
            )}

            {/* Step 1: Add Questions */}
            {currentStep === 1 && (
  <>
    {/* Show added questions summary when in edit mode */}
    {isEditMode && assessment?.questions?.length > 0 && (
      <>
        {/* Header outside the card */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", fontSize: "17px" }}
          >
            {assessment?.questions?.length} {` Questions`}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ background: "white", textTransform: "none" }}
            onClick={() =>
              navigate(`/self-assessments/questions/${assessmentId}`)
            }
          >
            {`Manage Questions`}
          </Button>
        </Box>

        {/* Card with questions list */}
        {/* <Card variant="outlined" sx={{ mb: 4, bgcolor: "white" }}> */}
          <CardContent>
            <Stack spacing={1} sx={{ maxHeight: 300, overflow: "auto" }}>
              {assessment?.questions?.map(
                (question: any, index: number) => (
                  <Box
                    key={question._id}
                    sx={{
                      px: 2, py:1.5,
                      bgcolor: "white",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">
                          {index + 1}. {question.question?.text || ""}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )
              )}
            </Stack>
          </CardContent>
        {/* </Card> */}
      </>
    )}

    {/* Empty state for new assessment or when no questions added */}
    {!isEditMode || !assessment?.questions?.length ? (
      <Box
        sx={{
          p: 4,
          bgcolor: "#f5f5f5",
          borderRadius: 1,
          textAlign: "center",
          border: "2px dashed #ccc",
        }}
      >
        <Typography
          variant="body1"
          sx={{ mb: 3, color: "text.secondary" }}
        >
          {isEditMode
            ? `Add questions to enhance your assessment`
            : `Click the button below to start adding questions`}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ textTransform: "none" }}
          onClick={() =>
            navigate(`/self-assessments/questions/${assessmentId}`)
          }
        >
          {`Add Questions`}
        </Button>
      </Box>
    ) : (
      <Box>{/* Optional: Add any additional content here */}</Box>
    )}
  </>
)}

            {/* Step 2: Publish */}
            {currentStep === 2 && (
              <>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                  {t`Review and Publish`}
                </Typography>
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Assessment Name`}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">{formData.name}</Typography>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Assessment Category`}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">{formData.category}</Typography>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Time Limit`}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">
                          {formData.time} {t`minutes`}
                        </Typography>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Description`}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">{formData.description}</Typography>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Questions Added`}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">{assessment?.questions?.length || 0}</Typography>
                      </Grid>
                    </Grid>

                  </CardContent>
                </Card>
              </>
            )}

            {/* Action Buttons */}
            <Box
  sx={{
    display: "flex",
    gap: 2,
    justifyContent: "flex-end",
    mt: 4,
  }}
>
  <Button
    variant="outlined"
    onClick={handleBack}
    disabled={loading}
    sx={{ textTransform: "none" }}
  >
    {currentStep === 0 ? t`Cancel` : t`Back`}
  </Button>

  {currentStep === 0 && (
    <Button
      variant="contained"
      color="primary"
      onClick={handleNextStep}
      disabled={loading || !formData.name.trim()}
      sx={{ textTransform: "none" }}
    >
      {isEditMode ? t`Update and Next` : t`Next`}
    </Button>
  )}

  {currentStep === 1 && (
    <Button
      variant="contained"
      color="primary"
      onClick={handleNextStep}
      disabled={loading}
      sx={{ textTransform: "none" }}
    >
      {t`Next`}
    </Button>
  )}

  {currentStep === 2 && (
    <>
      <Button
        variant="outlined"
        onClick={handleSaveAsDraft}
        disabled={loading}
        sx={{ textTransform: "none" }}
      >
        {t`Save as Draft`}
      </Button>
      <Button
        variant="contained"
        color="success"
        startIcon={<PublishIcon />}
        onClick={handlePublish}
        disabled={loading}
        sx={{ textTransform: "none" }}
      >
        {t`Publish`}
      </Button>
    </>
  )}
</Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default CreateEditSelfAssessment;